// ============================================================
// threeWayMerge — 予約編集パネルの三方向マージ（純粋関数・React 非依存）
//
// 背景（2026-09-07）:
//   ブラウザ X が予約 M のパネルを開いたまま、ブラウザ Y が M を保存すると、
//   X の保存が「パネルを開いた時点の全フィールド」を送るため Y の変更を
//   先祖返りさせていた（`{...fresh, ...editing}` は editing 側が全勝する）。
//
// 解法: git と同じ三方向マージ。
//   base   = パネルを開いた時点の値（共通祖先）
//   mine   = パネルで編集中の値
//   theirs = サーバーの最新値（保存直前に fresh fetch / Pusher でキャッシュに届いた値）
//
//   フィールドごとに
//     - 自分だけが変えた      → mine を採用
//     - 相手だけが変えた      → theirs を採用（＝上書きしない）
//     - 両方が別の値に変えた  → 競合。利用者に確認（resolutions で解決）
//   メモは追記型なので、双方が「末尾に追記」しただけなら両方を連結して両立させる。
//
// 表現差の吸収:
//   base/mine は toEditorReservation 済みの editor 形（license: true, pax: 2 ...）、
//   theirs は mapReservation 済みの生レコード（license: 1, pax: "2" ...）のことがある。
//   比較は normalizeValue() で表現を揃えてから行う。
//
// detail=null 予約への配慮:
//   theirs にそのキーが存在しない場合（detail 未ロード / detail=null）は
//   「相手が消した」ではなく「情報なし」として扱う（hasField で判定）。
//   2026-03-28 の「部分データで全フィールド空上書き」事故と同型を避けるため。
// ============================================================

import { getCodeById } from '../../../lib/statusMapper';

export type FieldKind = 'text' | 'number' | 'bool' | 'date' | 'array' | 'json' | 'memo' | 'id' | 'status';

export interface MergeFieldDef {
  field: string;
  /** パネルの表示ラベル（競合ダイアログ・変更サマリーで使う） */
  label: string;
  kind: FieldKind;
  /** detail サブオブジェクトに属するフィールド（root と detail の両方に書き戻す） */
  detail?: boolean;
  /** 価格として ¥ 表示する */
  price?: boolean;
}

/**
 * マージ対象フィールド。編集パネルで変更しうるものだけを列挙する。
 * root / detail の区分は dispatchService.buildDetailPayload に合わせる。
 */
export const MERGE_FIELDS: readonly MergeFieldDef[] = [
  // --- root ---
  { field: 'customer_name', label: '予約名', kind: 'text' },
  { field: 'contact_code', label: '予約コード', kind: 'text' },
  { field: 'customer_email', label: 'メール', kind: 'text' },
  { field: 'customer_tel', label: '電話番号', kind: 'text' },
  { field: 'customer_address', label: '住所', kind: 'text' },
  { field: 'start_date', label: '貸出日時', kind: 'date' },
  { field: 'end_date', label: '返却日時', kind: 'date' },
  { field: 'request_coming_time', label: '来店リクエスト時間', kind: 'text' },
  { field: 'request_returning_time', label: '返却リクエスト時間', kind: 'text' },
  { field: 'status', label: 'ステータス', kind: 'status' },
  { field: 'vehicle_id', label: '車両', kind: 'id' },
  { field: 'car_type', label: '予約車両タイプ', kind: 'text' },
  { field: 'short_memo', label: 'ショートメモ', kind: 'text' },
  { field: 'bg_color', label: '背景色', kind: 'text' },
  { field: 'text_color', label: '文字色', kind: 'text' },
  { field: 'mail_send_flg1', label: '予約確定メール', kind: 'bool' },
  { field: 'mail_send_flg2', label: '決済御礼メール', kind: 'bool' },
  { field: 'mail_send_flg3', label: '利用御礼メール', kind: 'bool' },
  { field: 'json_data', label: 'JSONデータ', kind: 'json' },
  { field: 'rental_location', label: '貸出場所', kind: 'text' },
  { field: 'return_location', label: '返却場所', kind: 'text' },
  // --- detail ---
  { field: 'contact_id', label: '連絡先ID', kind: 'text', detail: true },
  { field: 'contact_media', label: '予約媒体', kind: 'text', detail: true },
  { field: 'contact_plan', label: 'プラン名', kind: 'text', detail: true },
  { field: 'communication_tools', label: '連絡手段', kind: 'array', detail: true },
  { field: 'pax', label: '大人', kind: 'number', detail: true },
  { field: 'pax_children', label: '子供', kind: 'number', detail: true },
  { field: 'child_seat', label: 'チャイルドシート', kind: 'number', detail: true },
  { field: 'booster_seat', label: 'ブースターシート', kind: 'number', detail: true },
  { field: 'junior_seat', label: 'ジュニアシート', kind: 'number', detail: true },
  { field: 'arrival_flight', label: '到着便', kind: 'text', detail: true },
  { field: 'departure_flight', label: '出発便', kind: 'text', detail: true },
  { field: 'base_price', label: '基本料金', kind: 'number', detail: true, price: true },
  { field: 'option', label: 'オプション内容', kind: 'text', detail: true },
  { field: 'option_price', label: 'オプション料金', kind: 'number', detail: true, price: true },
  { field: 'noc', label: 'NOC', kind: 'bool', detail: true },
  { field: 'noc_price', label: 'NOC料金', kind: 'number', detail: true, price: true },
  { field: 'cdw', label: 'CDW', kind: 'bool', detail: true },
  { field: 'cdw_price', label: 'CDW料金', kind: 'number', detail: true, price: true },
  { field: 'coupon', label: 'クーポン', kind: 'text', detail: true },
  { field: 'point', label: 'ポイント', kind: 'number', detail: true },
  { field: 'sum_price', label: '合計金額', kind: 'number', detail: true, price: true },
  { field: 'days', label: '日数', kind: 'number', detail: true },
  { field: 'nights', label: '泊数', kind: 'number', detail: true },
  { field: 'invoice_link', label: 'Square請求書ID', kind: 'text', detail: true },
  { field: 'comment', label: 'コメント', kind: 'text', detail: true },
  { field: 'memo', label: 'メモ', kind: 'memo', detail: true },
  { field: 'emergency_name', label: '緊急連絡先（氏名）', kind: 'text', detail: true },
  { field: 'emergency_rel', label: '緊急連絡先（続柄）', kind: 'text', detail: true },
  { field: 'emergency_tel', label: '緊急連絡先（電話）', kind: 'text', detail: true },
  { field: 'license', label: '免許確認', kind: 'bool', detail: true },
  { field: 'payment', label: '支払い確認', kind: 'bool', detail: true },
  { field: 'terms_and_conditions', label: '利用規約', kind: 'bool', detail: true },
  { field: 'disclaimer', label: '免責事項', kind: 'bool', detail: true },
  { field: 'agreement', label: '契約同意', kind: 'bool', detail: true },
];

const FIELD_DEF_MAP: Record<string, MergeFieldDef> = Object.fromEntries(
  MERGE_FIELDS.map((d) => [d.field, d]),
);

export function getFieldDef(field: string): MergeFieldDef | undefined {
  return FIELD_DEF_MAP[field];
}

export function fieldLabel(field: string): string {
  return FIELD_DEF_MAP[field]?.label ?? field;
}

type AnyRecord = Record<string, any>;

/** root → detail の順で値を取り出す（mapReservation は detail を root に展開しているため root 優先） */
export function getField(obj: AnyRecord | null | undefined, field: string): any {
  if (!obj) return undefined;
  if (obj[field] !== undefined) return obj[field];
  const detail = obj.detail;
  if (detail && typeof detail === 'object' && detail[field] !== undefined) return detail[field];
  return undefined;
}

/** そのレコードがフィールドの情報を持っているか（detail 未ロードのキャッシュ対策） */
export function hasField(obj: AnyRecord | null | undefined, field: string): boolean {
  return getField(obj, field) !== undefined;
}

const TRUE_SET = new Set(['1', 'true']);

function stableStringify(v: any): string {
  if (v === null || v === undefined) return '';
  if (typeof v !== 'object') return String(v);
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(',')}]`;
  const keys = Object.keys(v).sort();
  return `{${keys.map((k) => `${k}:${stableStringify(v[k])}`).join(',')}}`;
}

/**
 * 比較用に値を正規化する。editor 形（true / 2 / ISO）と生レコード（1 / "2" / "2026-..."）を
 * 同一視するのが目的。表示用ではない。
 */
export function normalizeValue(value: any, kind: FieldKind): string {
  if (value === null || value === undefined) return kind === 'number' || kind === 'bool' ? '0' : '';
  switch (kind) {
    case 'bool': {
      if (typeof value === 'boolean') return value ? '1' : '0';
      const s = String(value).trim().toLowerCase();
      if (s === '') return '0';
      if (TRUE_SET.has(s)) return '1';
      const n = Number(s);
      return Number.isFinite(n) && n !== 0 ? '1' : '0';
    }
    case 'number': {
      const s = String(value).trim();
      if (s === '') return '0';
      const n = Number(s);
      return Number.isFinite(n) ? String(n) : s;
    }
    case 'id': {
      const s = String(value).trim();
      if (s === '') return '';
      const n = Number(s);
      return Number.isFinite(n) ? String(n) : s;
    }
    case 'status': {
      // editor は code（'booked'）、即時保存後は id（'1'）を持つことがあるので code に揃える
      const s = String(value).trim();
      if (s === '') return '';
      if (/^\d+$/.test(s)) return getCodeById(s) || s;
      return s;
    }
    case 'date': {
      const s = String(value).trim();
      if (s === '') return '';
      const t = new Date(s).getTime();
      return Number.isNaN(t) ? s : String(t);
    }
    case 'array': {
      const arr = Array.isArray(value) ? value : [];
      return arr.map((x) => String(x)).sort().join(',');
    }
    case 'json': {
      let v = value;
      if (typeof v === 'string') {
        const s = v.trim();
        if (s === '') return '';
        try { v = JSON.parse(s); } catch { return s; }
      }
      return stableStringify(v);
    }
    case 'memo':
    case 'text':
    default:
      return String(value).replace(/\r\n/g, '\n').trim();
  }
}

export function valuesEqual(def: MergeFieldDef, a: any, b: any): boolean {
  return normalizeValue(a, def.kind) === normalizeValue(b, def.kind);
}

/** a → b で変わったフィールド（b にキーが存在するものだけ） */
export function diffFields(a: AnyRecord, b: AnyRecord): string[] {
  const out: string[] = [];
  for (const def of MERGE_FIELDS) {
    if (!hasField(b, def.field)) continue;
    if (!valuesEqual(def, getField(a, def.field), getField(b, def.field))) out.push(def.field);
  }
  return out;
}

// ---------------- メモの三方向マージ ----------------

function normMemo(v: any): string {
  return v == null ? '' : String(v).replace(/\r\n/g, '\n');
}

function joinMemo(head: string, tail: string): string {
  if (!head) return tail.replace(/^\n+/, '');
  if (!tail) return head;
  return tail.startsWith('\n') || head.endsWith('\n') ? head + tail : `${head}\n${tail}`;
}

export type MemoMergeResult =
  | { kind: 'merged'; value: string }
  | { kind: 'conflict' };

/**
 * メモは追記型（タイムライン）なので、双方が base の末尾に追記しただけなら連結できる。
 * 既存ブロックの書き換え（メモブロック編集）が絡む場合は競合とする。
 */
export function mergeMemo(base: any, mine: any, theirs: any): MemoMergeResult {
  const b = normMemo(base);
  const m = normMemo(mine);
  const t = normMemo(theirs);
  if (m.trim() === b.trim()) return { kind: 'merged', value: t };
  if (t.trim() === b.trim()) return { kind: 'merged', value: m };
  if (m.trim() === t.trim()) return { kind: 'merged', value: m };
  if (isAppendOf(b, m) && isAppendOf(b, t)) {
    const myAdd = m.slice(b.length);
    return { kind: 'merged', value: joinMemo(t, myAdd) };
  }
  return { kind: 'conflict' };
}

/**
 * next が base の「末尾に行を追記」した形か。
 * 追記は必ず改行区切りで行われる（appendMemoEntry / appendMemoAction）ため、
 * 「初回メモ」→「初回メモ（修正）」のような同一行の書き換えは追記とみなさない。
 */
function isAppendOf(base: string, next: string): boolean {
  if (!next.startsWith(base)) return false;
  if (base === '' || base.endsWith('\n')) return true;
  return next.slice(base.length).startsWith('\n');
}

/** theirs が base に追記した部分（相手の変更記録の表示用）。追記でなければ null */
export function memoAddedByTheirs(base: any, theirs: any): string | null {
  const b = normMemo(base);
  const t = normMemo(theirs);
  if (t.length <= b.length || !isAppendOf(b, t)) return null;
  return t.slice(b.length).replace(/^\n+/, '');
}

// ---------------- 三方向マージ本体 ----------------

export type Resolution = 'mine' | 'theirs';

export interface FieldConflict {
  field: string;
  label: string;
  base: any;
  mine: any;
  theirs: any;
}

export interface ThreeWayMergeResult {
  /** 自分が変更した（base と異なる）フィールド */
  myChanges: string[];
  /** 相手が変更した（base と異なる、かつ theirs にキーが存在する）フィールド */
  theirChanges: string[];
  /** 双方が別の値に変更したフィールド（resolutions で解決済みのものも含む） */
  conflicts: FieldConflict[];
  /** resolutions が与えられていない競合 */
  unresolved: FieldConflict[];
  /** theirs を土台に、自分の変更（と解決済み競合）を載せたオブジェクト。unresolved が残る間は不完全 */
  merged: AnyRecord;
  /** merged に実際に載せた自分側の値（root キー → 値）。base 更新などに使う */
  applied: AnyRecord;
}

export interface ThreeWayMergeOptions {
  resolutions?: Record<string, Resolution>;
}

/**
 * @param base   パネルを開いた時点のスナップショット
 * @param mine   編集中の値
 * @param theirs サーバー最新（mapReservation 済み。detail を含んでよい）
 */
export function threeWayMerge(
  base: AnyRecord,
  mine: AnyRecord,
  theirs: AnyRecord,
  options: ThreeWayMergeOptions = {},
): ThreeWayMergeResult {
  const resolutions = options.resolutions || {};
  const myChanges: string[] = [];
  const theirChanges: string[] = [];
  const conflicts: FieldConflict[] = [];
  const unresolved: FieldConflict[] = [];
  const applied: AnyRecord = {};
  const appliedDetail: AnyRecord = {};

  const apply = (def: MergeFieldDef, value: any) => {
    applied[def.field] = value;
    if (def.detail) appliedDetail[def.field] = value;
  };

  for (const def of MERGE_FIELDS) {
    const f = def.field;
    const b = getField(base, f);
    const m = getField(mine, f);
    const t = getField(theirs, f);
    const theirsPresent = hasField(theirs, f);
    const mineChanged = !valuesEqual(def, b, m);
    const theirsChanged = theirsPresent && !valuesEqual(def, b, t);

    if (theirsChanged) theirChanges.push(f);
    if (!mineChanged) continue;
    myChanges.push(f);

    if (!theirsChanged || valuesEqual(def, m, t)) {
      apply(def, m);
      continue;
    }

    // 双方が変更
    if (def.kind === 'memo') {
      const r = mergeMemo(b, m, t);
      if (r.kind === 'merged') {
        apply(def, r.value);
        continue;
      }
    }
    const conflict: FieldConflict = { field: f, label: def.label, base: b, mine: m, theirs: t };
    conflicts.push(conflict);
    const resolution = resolutions[f];
    if (resolution === 'mine') {
      apply(def, m);
    } else if (resolution === 'theirs') {
      // theirs をそのまま残す（applied には載せない）
    } else {
      unresolved.push(conflict);
    }
  }

  const merged: AnyRecord = { ...theirs, ...applied };
  if (Object.keys(appliedDetail).length > 0 || (theirs.detail && typeof theirs.detail === 'object')) {
    merged.detail = { ...((theirs.detail as AnyRecord) || {}), ...appliedDetail };
  }

  return { myChanges, theirChanges, conflicts, unresolved, merged, applied };
}

// ---------------- 表示用フォーマット ----------------

export interface StatusLike {
  id: number | string;
  label?: string;
  name?: string;
  code?: string;
}

const COMM_TOOL_LABELS: Record<string, string> = {
  1: 'TEL', 2: 'SMS', 3: 'LINE', 4: 'WhatsApp', 5: 'Instagram',
};

/** 競合ダイアログ・変更サマリー向けの人間可読な値 */
export function formatFieldValue(field: string, value: any, statusOptions?: StatusLike[]): string {
  const def = FIELD_DEF_MAP[field];
  if (value === null || value === undefined || value === '') {
    return def && (def.kind === 'bool') ? '✗' : '（空）';
  }
  if (!def) return String(value);
  if (field === 'status' && statusOptions) {
    const s = statusOptions.find((st) => String(st.id) === String(value) || st.code === value);
    if (s) return s.label || s.name || String(value);
  }
  switch (def.kind) {
    case 'bool':
      return normalizeValue(value, 'bool') === '1' ? '✓' : '✗';
    case 'number': {
      const n = Number(value);
      if (!Number.isFinite(n)) return String(value);
      return def.price ? `¥${n.toLocaleString()}` : n.toLocaleString();
    }
    case 'date': {
      const d = new Date(String(value));
      if (Number.isNaN(d.getTime())) return String(value);
      const p = (x: number) => String(x).padStart(2, '0');
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
    }
    case 'array': {
      const arr = Array.isArray(value) ? value : [];
      return arr.length ? arr.map((x) => COMM_TOOL_LABELS[String(x)] || String(x)).join(', ') : '（なし）';
    }
    case 'json':
      return typeof value === 'string' ? value : JSON.stringify(value);
    case 'memo': {
      const s = String(value);
      return s.length > 120 ? `${s.slice(0, 120)}…` : s;
    }
    default:
      return String(value);
  }
}

/** 相手のレコードが base より新しいか（updated_at で判定。判定材料が無ければ true） */
export function isNewerThanBase(theirs: AnyRecord | null | undefined, base: AnyRecord | null | undefined): boolean {
  const t = theirs?.updated_at;
  const b = base?.updated_at;
  if (!t || !b) return true;
  const tt = new Date(t).getTime();
  const bt = new Date(b).getTime();
  if (Number.isNaN(tt) || Number.isNaN(bt)) return true;
  return tt > bt;
}
